// ─── La partita e chi l'ha organizzata, nella scheda (fase 2, 26/09/2026) ────
//
// Dal mockup approvato («3 bottone, 4 organizzatore»):
//   · R12 — **chi manca lo paga l'organizzatore**: la quota di chi non c'è va
//     sulla sua riga, col bottone. Solo a partita cominciata: solo al circolo si
//     sa chi si è presentato davvero;
//   · 🚫 **«Segna no-show»** su ogni partita, anche di giorno e nel weekend:
//     conta sull'organizzatore (per la fase B è un'infrazione);
//   · ↩️ **«Annulla il no-show»** (voce 30), col perché;
//   · 🚦 le regole della fase B di chi l'ha organizzata.
//
// 🔄 30/09/2026 — via l'acconto: niente più sezione dell'acconto accanto, né il
// lucchetto dei no-show ripetuti (R16), che chiedeva l'acconto.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETICHETTE_INFRAZIONE,
  oraDiRoma,
  formatCents,
  giornoPerEsteso,
  type OrganizzatoreDellaPartita,
  type SchedaPrenotazione,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

export function PartitaDellaScheda({
  p,
  puoIncassare,
}: {
  p: SchedaPrenotazione;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [conferma, setConferma] = useState(false);
  const [annulla, setAnnulla] = useState(false);
  const [motivo, setMotivo] = useState('');
  const dati = useQuery({
    queryKey: ['organizzatore-partita', p.id],
    queryFn: () => chiama<OrganizzatoreDellaPartita>('GET', `/bookings/${p.id}/organizer`),
    enabled: p.type === 'partita',
  });
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: ['organizzatore-partita', p.id] });
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
    void qc.invalidateQueries({ queryKey: ['calendario'] });
  };
  const carica = useMutation({
    meta: { fatto: 'quote mancanti messe a carico dell’organizzatore' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/missing-to-organizer`),
    onSuccess: rileggi,
  });
  const noShow = useMutation({
    meta: { fatto: 'no-show segnato' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/no-show`),
    onSuccess: () => {
      setConferma(false);
      rileggi();
    },
  });

  const annullo = useMutation({
    meta: { fatto: 'no-show annullato' },
    mutationFn: () => chiama('POST', `/bookings/${p.id}/no-show/cancel`, { reason: motivo.trim() }),
    onSuccess: () => {
      setAnnulla(false);
      setMotivo('');
      rileggi();
    },
  });

  if (p.type !== 'partita' || !dati.data) return null;
  const {
    missing: q,
    noShow: segnato,
    noShowCancelled: annullato,
    rules: regole,
    infraction: infrazione,
  } = dati.data;
  const cominciata = Date.parse(p.startsAt) <= Date.now();
  const puoSegnare = puoIncassare && cominciata && !segnato && p.status !== 'cancelled';
  const organizzatore = p.players.find((g) => g.memberId !== null && g.memberId === p.organizerMemberId);

  // 🚦 Fase B: con le regole vive, lo stato di chi l'ha organizzata c'è sempre.
  const conRegole = regole !== null && regole.state !== 'spento';
  if (!q && !puoSegnare && !segnato && !annullato && !conRegole && !infrazione) return null;

  return (
    <div className="caparra-scheda">
      {conRegole && regole && (
        <p className="piccolo regole-partita">
          Chi l’ha organizzata:{' '}
          <span className={`pill-caparra ${regole.status === 'obbligo_carta' ? 'pill-attesa' : 'pill-ok'}`}>
            {regole.status === 'obbligo_carta'
              ? `Obbligo carta · ${regole.regularGames} su ${regole.needed}`
              : 'Libero'}
          </span>
          {' · '}disdetta gratis fino a {giornoPerEsteso(regole.freeUntil.date)}, {regole.freeUntil.time}
          {Date.parse(p.startsAt) > Date.now() && dentroLeOreDiRoma(regole.freeUntil) ? (
            <strong> · scaduta</strong>
          ) : null}
        </p>
      )}
      {infrazione && (
        <p className="avvertenza piccolo">
          {ETICHETTE_INFRAZIONE[infrazione.kind]} registrata il{' '}
          {new Date(infrazione.recordedAt).toLocaleDateString('it-IT')}
          {infrazione.recordedBy ? ` da ${infrazione.recordedBy}` : ' dal bot'}
          {infrazione.counted ? '.' : ' — nel «solo registro»: non conta.'} La si annulla dalla scheda del
          socio.
        </p>
      )}

      {q && cominciata && (
        <div className="finestra-caparra">
          {q.done ? (
            <p>
              ✓ La quota di chi manca è a carico di <strong>{q.organizerName}</strong>:{' '}
              {formatCents(q.organizerTotalCents)}.
            </p>
          ) : (
            <>
              <p>
                <strong>{q.missing === 1 ? 'Manca 1 giocatore' : `Mancano ${q.missing} giocatori`}.</strong>{' '}
                {q.missing === 1 ? 'La sua quota' : 'La loro quota'} ({q.missing} ×{' '}
                {formatCents(q.quotaCents)}) va a carico di chi ha organizzato.
              </p>
              {puoIncassare && (
                <div className="azioni-caparra">
                  <button type="button" disabled={carica.isPending} onClick={() => carica.mutate()}>
                    Metti {formatCents(q.quotaCents * q.missing)} a carico di {q.organizerName}
                  </button>
                </div>
              )}
              <Guasto errore={carica.error} />
            </>
          )}
        </div>
      )}

      {segnato && <p className="tenue piccolo">🚫 Segnata come no-show.</p>}
      {segnato && puoIncassare && !annulla && (
        <div className="azioni-caparra">
          <button type="button" className="bordato" onClick={() => setAnnulla(true)}>
            ↩️ Annulla il no-show
          </button>
        </div>
      )}
      {annulla && (
        <div className="finestra-caparra">
          <strong>↩️ Annulla il no-show</strong>
          <p>
            Il no-show non conta più su {organizzatore?.displayName ?? 'chi l’ha organizzata'}, e con lui se
            ne va l’infrazione che era nata.
          </p>
          <label className="campo">
            <span>Perché lo annulli (resta scritto)</span>
            <input
              id="motivo-annullo-no-show"
              value={motivo}
              placeholder="per esempio: erano in campo, segnata per sbaglio"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={motivo.trim().length < 3 || annullo.isPending}
              onClick={() => annullo.mutate()}
            >
              Annulla il no-show
            </button>
            <button type="button" className="bordato" onClick={() => setAnnulla(false)}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={annullo.error} />
        </div>
      )}
      {!segnato && annullato && (
        <p className="annullo-no-show">
          ↩️ No-show annullato il{' '}
          {new Date(annullato.at).toLocaleString('it-IT', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {annullato.by ? ` da ${annullato.by}` : ''}: «{annullato.reason}»
        </p>
      )}
      {puoSegnare && !conferma && (
        <div className="azioni-caparra">
          <button type="button" className="bordato" onClick={() => setConferma(true)}>
            🚫 Segna no-show
          </button>
        </div>
      )}
      {conferma && (
        <div className="finestra-caparra">
          <strong>🚫 Non si sono presentati</strong>
          <p>
            Conta su chi ha organizzato: con le regole della disdetta accese è un’infrazione, e il bot glielo
            dice.
          </p>
          <div className="azioni-caparra">
            <button type="button" disabled={noShow.isPending} onClick={() => noShow.mutate()}>
              Segna no-show
            </button>
            <button type="button" className="bordato" onClick={() => setConferma(false)}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={noShow.error} />
        </div>
      )}
    </div>
  );
}

/**
 * 🎓 **La trattenuta al maestro già scritta** su questa lezione, che si toglie
 * col motivo. 🔄 30/09/2026: con l'acconto è andata la regola della sera che le
 * scriveva (la lezione disdetta a meno di 48 ore, l'allievo che non viene): qui
 * restano quelle di prima.
 */
export function LezioneDellaScheda({
  p,
  puoIncassare,
}: {
  p: SchedaPrenotazione;
  puoIncassare: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [chiede, setChiede] = useState(false);
  const [motivo, setMotivo] = useState('');
  const togli = useMutation({
    meta: { fatto: 'trattenuta al maestro tolta' },
    mutationFn: () =>
      chiama('POST', `/instructor-deductions/${p.trattenuta!.id}/remove`, { reason: motivo.trim() }),
    onSuccess: () => {
      setChiede(false);
      setMotivo('');
      void qc.invalidateQueries({ queryKey: ['prenotazione', p.id] });
      void qc.invalidateQueries({ queryKey: ['conto-maestri'] });
    },
  });
  const t = p.trattenuta ?? null;
  if (!t) return null;
  return (
    <div className="caparra-scheda">
      <p className="avvertenza">
        🎓{' '}
        <strong>
          {formatCents(t.amountCents)} dal compenso di {p.instructorName ?? 'chi la tiene'}
        </strong>
        : {t.reason === 'no_show' ? 'l’allievo non si è presentato' : 'disdetta a meno di 48 ore'}.
      </p>
      {puoIncassare && !chiede && (
        <div className="azioni-caparra">
          <button type="button" className="bordato" onClick={() => setChiede(true)}>
            Togli la trattenuta
          </button>
        </div>
      )}
      {chiede && (
        <div className="finestra-caparra">
          <label className="campo">
            <span>Perché la togli (resta scritto)</span>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </label>
          <Guasto errore={togli.error} />
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={togli.isPending || motivo.trim().length < 3}
              onClick={() => togli.mutate()}
            >
              Togli la trattenuta
            </button>
            <button type="button" className="bordato" onClick={() => setChiede(false)}>
              Lascia stare
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Il termine della disdetta gratis è già passato, sull'orologio di Roma? */
function dentroLeOreDiRoma(t: { date: string; time: string }): boolean {
  const adesso = oraDiRoma(new Date());
  return `${adesso.data} ${adesso.ora}` >= `${t.date} ${t.time}`;
}
